import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import api from '@/lib/api';

/* ============================================================================
 * AnimatedCounter — counts a number from 0 → target when scrolled into view.
 * Used in the marketing landing page hero stats strip.
 * ========================================================================== */
export function AnimatedCounter({ value, suffix = '', prefix = '', duration = 1.4, className = '', testid }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-30% 0px' });
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (latest) => Math.floor(latest));
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(mv, value, { duration, ease: 'easeOut' });
        const unsub = rounded.on('change', (v) => setDisplay(v));
        return () => {
            controls.stop();
            unsub();
        };
    }, [inView, value, duration, mv, rounded]);

    return (
        <span ref={ref} className={className} data-testid={testid}>
            {prefix}{display.toLocaleString()}{suffix}
        </span>
    );
}

/* ============================================================================
 * CoachGalleryCarousel — "Instructors using the platform" social proof.
 *
 * Fetches up to 30 active coaches with profile photos and displays them in a
 * horizontal-scrolling, swipeable card row. Each card shows the coach's
 * profile photo, name, and city/state overlay. Cards link to the coach's
 * public page so visitors can browse what a SplashPad-powered school looks
 * like in the wild.
 *
 * Design notes:
 *   - Pure CSS scroll-snap for smooth swipe on touch devices (no JS gesture).
 *   - Prev/Next buttons appear on hover for desktop users.
 *   - Initials fallback chip when a coach has no photo.
 *   - Gracefully hides itself if the API returns < 4 coaches (looks bare).
 * ========================================================================== */
export function CoachGalleryCarousel() {
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failedPhotos, setFailedPhotos] = useState({});
    const scrollRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.get('/public/landing-coaches')
            .then((r) => { if (!cancelled) setCoaches(r?.data?.coaches || []); })
            .catch(() => { if (!cancelled) setCoaches([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const scrollBy = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        const cardWidth = 240; // approximate card + gap
        el.scrollBy({ left: dir * cardWidth * 2, behavior: 'smooth' });
    };

    if (loading) {
        return (
            <div className="flex gap-3 overflow-hidden px-1" data-testid="landing-coach-gallery-loading">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-72 w-52 shrink-0 animate-pulse rounded-3xl bg-white/60" />
                ))}
            </div>
        );
    }

    // Hide section only if we have ZERO coaches with photos — anything 1+
    // is real social proof worth showing. The carousel still looks good
    // with as few as a single card because it uses CSS scroll-snap, not a
    // fixed grid, so it sizes itself to whatever's there.
    if (coaches.length < 1) {
        return null;
    }

    // Only show the scroll arrows when there's actually content to scroll
    // through. With 4 or fewer cards everything fits on screen so arrows
    // would be confusing dead UI.
    const showArrows = coaches.length > 4;

    return (
        <div className="relative" data-testid="landing-coach-gallery-carousel">
            <div className="pointer-events-none absolute -left-2 top-0 bottom-2 z-10 hidden w-12 bg-gradient-to-r from-[#f4f7fb] to-transparent md:block" />
            <div className="pointer-events-none absolute -right-2 top-0 bottom-2 z-10 hidden w-12 bg-gradient-to-l from-[#f4f7fb] to-transparent md:block" />

            <button
                type="button"
                onClick={() => scrollBy(-1)}
                className={`absolute -left-3 top-1/2 z-20 -translate-y-1/2 items-center justify-center rounded-full bg-white p-2 shadow-md ring-1 ring-[#0a1628]/10 transition hover:scale-105 hover:shadow-lg ${showArrows ? 'hidden md:flex' : 'hidden'}`}
                aria-label="Scroll left"
                data-testid="landing-coach-gallery-prev"
            >
                <ChevronLeft className="h-5 w-5 text-[#0a1628]" />
            </button>
            <button
                type="button"
                onClick={() => scrollBy(1)}
                className={`absolute -right-3 top-1/2 z-20 -translate-y-1/2 items-center justify-center rounded-full bg-white p-2 shadow-md ring-1 ring-[#0a1628]/10 transition hover:scale-105 hover:shadow-lg ${showArrows ? 'hidden md:flex' : 'hidden'}`}
                aria-label="Scroll right"
                data-testid="landing-coach-gallery-next"
            >
                <ChevronRight className="h-5 w-5 text-[#0a1628]" />
            </button>

            <div
                ref={scrollRef}
                // Center the row when we have fewer cards than typically fill
                // a desktop viewport (~5 cards) — otherwise a single coach
                // looks orphaned in the top-left. With 5+ cards the natural
                // left-alignment is correct for horizontal scroll affordance.
                className={`flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 pl-1 pr-1 ${coaches.length < 5 ? 'justify-center' : ''}`}
                style={{ scrollbarWidth: 'none' }}
            >
                <style>{`[data-testid="landing-coach-gallery-carousel"] > div::-webkit-scrollbar { display: none; }`}</style>
                {coaches.map((c, i) => (
                    <motion.div
                        key={c.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-10% 0px' }}
                        transition={{ duration: 0.45, delay: Math.min(i * 0.04, 0.4) }}
                        className="h-72 w-52 shrink-0 snap-start"
                    >
                        {/* Phase 44 (wingman audit fix): The whole card is a
                            plain <a> now. Previously the link was on a
                            `motion.a` with `whileInView` animations + the
                            `<img>` covering it absolutely — taps on the
                            photo got swallowed by framer-motion's gesture
                            handler on mobile, so only the text under the
                            gradient (which is on top of the image stack)
                            worked. Also removed `target="_blank"` so
                            popup-blockers can't reject the navigation. */}
                        <a
                            href={`/coach/${c.slug}`}
                            className="group relative block h-full w-full overflow-hidden rounded-3xl bg-[#0a1628] shadow-[0_15px_40px_rgba(10,22,40,0.18)] ring-1 ring-white/10 transition-transform hover:scale-[1.02]"
                            data-testid={`landing-coach-card-${c.slug}`}
                            aria-label={`View ${c.display_name}'s SplashPad coach page`}
                        >
                            {c.photo_url && !failedPhotos[c.photo_url] ? (
                                <img
                                    src={c.photo_url}
                                    alt={`${c.display_name} — SplashPad coach`}
                                    className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                    onError={() => setFailedPhotos((failed) => ({ ...failed, [c.photo_url]: true }))}
                                    data-testid={`landing-coach-photo-${c.slug}`}
                                />
                            ) : (
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1a3a5c] to-[#0a1628]" data-testid={`landing-coach-photo-fallback-${c.slug}`}>
                                    <div className="font-display text-6xl font-bold text-[#00d4ff]/40">
                                        {(c.display_name || 'C').split(/\s+/).map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                                    </div>
                                </div>
                            )}
                            {/* Gradient bottom for legibility of overlay text */}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0a1628]/95 via-[#0a1628]/75 to-transparent" />
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 text-white">
                                <div className="font-display text-base font-bold leading-tight" data-testid={`landing-coach-card-name-${c.slug}`}>{c.display_name}</div>
                                {c.location && (
                                    <div className="mt-1 flex items-center gap-1 text-[11px] text-white/80">
                                        <MapPin className="h-3 w-3" />
                                        <span className="truncate">{c.location}</span>
                                    </div>
                                )}
                                {c.plan === 'pro' && (
                                    <div className="mt-2 inline-flex items-center rounded-full bg-[#ffd700] px-2 py-0.5 text-[10px] font-semibold text-[#0a1628]">PRO</div>
                                )}
                            </div>
                        </a>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
